import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Link, useNavigate } from 'react-router-dom';
import { CreditAllocationModal } from '@bri/shared-components';
import { formatWorkspaceRole } from '@/lib/workspaceRoles';
import { workspaceService } from '@/lib/services/workspaceService';
import { billingService, type UserCredits, type WorkspaceCredits } from '@/lib/services/billingService';
import {
  PlusIcon,
  CheckIcon,
  UsersIcon,
  PencilSquareIcon,
  CreditCardIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/lib/utils';

type EditWorkspaceState = {
  workspaceId: string;
  name: string;
  description: string;
};

type AllocationModalState = {
  isOpen: boolean;
  workspaceId: string | null;
  workspaceName: string;
};

export default function WorkspacesPage() {
  const { currentWorkspace, workspaces, switchWorkspace, refreshWorkspaces } = useWorkspace();
  const navigate = useNavigate();
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const [actionWorkspaceId, setActionWorkspaceId] = useState<string | null>(null);
  const [editWorkspace, setEditWorkspace] = useState<EditWorkspaceState | null>(null);
  const [isSavingWorkspace, setIsSavingWorkspace] = useState(false);
  const [userCredits, setUserCredits] = useState<UserCredits | null>(null);
  const [workspaceCredits, setWorkspaceCredits] = useState<Record<string, WorkspaceCredits>>({});
  const [isCreditsLoading, setIsCreditsLoading] = useState(false);
  const [isAllocatingCredits, setIsAllocatingCredits] = useState(false);
  const [allocationModal, setAllocationModal] = useState<AllocationModalState>({
    isOpen: false,
    workspaceId: null,
    workspaceName: '',
  });

  const sortedWorkspaces = useMemo(
    () =>
      [...workspaces].sort((a, b) => {
        if (a.workspaceId === currentWorkspace?.workspaceId) return -1;
        if (b.workspaceId === currentWorkspace?.workspaceId) return 1;
        return a.name.localeCompare(b.name);
      }),
    [currentWorkspace?.workspaceId, workspaces]
  );

  const loadCreditsData = useCallback(async () => {
    if (workspaces.length === 0) {
      setUserCredits(null);
      setWorkspaceCredits({});
      return;
    }

    setIsCreditsLoading(true);
    try {
      const [creditsData, workspaceCreditResults] = await Promise.all([
        billingService.getUserCredits(),
        Promise.all(
          workspaces.map(async (workspace) => {
            try {
              const credits = await billingService.getWorkspaceCredits(workspace.workspaceId);
              return { workspaceId: workspace.workspaceId, credits };
            } catch (error) {
              console.error(`Failed to load credits for workspace ${workspace.workspaceId}:`, error);
              return null;
            }
          })
        ),
      ]);

      setUserCredits(creditsData);

      const nextWorkspaceCredits: Record<string, WorkspaceCredits> = {};
      workspaceCreditResults.forEach((result) => {
        if (result) {
          nextWorkspaceCredits[result.workspaceId] = result.credits;
        }
      });
      setWorkspaceCredits(nextWorkspaceCredits);
    } catch (error) {
      console.error('Failed to load workspace credit balances:', error);
      setUserCredits(null);
      setWorkspaceCredits({});
    } finally {
      setIsCreditsLoading(false);
    }
  }, [workspaces]);

  useEffect(() => {
    void loadCreditsData();
  }, [loadCreditsData]);

  const handleSwitchWorkspace = async (workspaceId: string) => {
    if (workspaceId === currentWorkspace?.workspaceId) return;

    setSwitchingTo(workspaceId);
    try {
      await switchWorkspace(workspaceId);
      toast.success('Workspace switched successfully');
    } catch (error) {
      toast.error('Failed to switch workspace');
    } finally {
      setSwitchingTo(null);
    }
  };

  const handleManageMembers = async (workspaceId: string) => {
    setActionWorkspaceId(workspaceId);
    try {
      if (workspaceId !== currentWorkspace?.workspaceId) {
        await switchWorkspace(workspaceId);
      }

      navigate('/app/team');
    } catch (error) {
      toast.error('Failed to open members for that workspace');
    } finally {
      setActionWorkspaceId(null);
    }
  };

  const handleEditWorkspace = (workspaceId: string) => {
    const workspace = workspaces.find((item) => item.workspaceId === workspaceId);
    if (!workspace) {
      return;
    }

    setEditWorkspace({
      workspaceId: workspace.workspaceId,
      name: workspace.name,
      description: workspace.description || '',
    });
  };

  const handleAllocateCredits = async (workspaceId: string) => {
    const workspace = workspaces.find((item) => item.workspaceId === workspaceId);
    if (!workspace) {
      toast.error('Workspace not found');
      return;
    }

    setAllocationModal({
      isOpen: true,
      workspaceId,
      workspaceName: workspace.name,
    });
  };

  const handleModalAllocate = async (amount: number) => {
    if (!allocationModal.workspaceId) {
      return;
    }

    setIsAllocatingCredits(true);
    try {
      await billingService.allocateCredits(allocationModal.workspaceId, amount);
      toast.success(`Allocated ${amount} credits to workspace!`);
      await loadCreditsData();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error) || 'Failed to allocate credits');
      throw error;
    } finally {
      setIsAllocatingCredits(false);
    }
  };

  const handleSaveWorkspace = async () => {
    if (!editWorkspace) {
      return;
    }

    if (!editWorkspace.name.trim()) {
      toast.error('Workspace name is required');
      return;
    }

    setIsSavingWorkspace(true);
    try {
      await workspaceService.updateWorkspace(editWorkspace.workspaceId, {
        name: editWorkspace.name.trim(),
        description: editWorkspace.description.trim() || undefined,
      });

      await refreshWorkspaces();
      setEditWorkspace(null);
      toast.success('Workspace updated successfully');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error) || 'Failed to update workspace');
    } finally {
      setIsSavingWorkspace(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader />

      <div className="flex flex-1">
        <AppSidebar />

        <main className="flex-1 py-8">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Workspaces</h1>
                <p className="mt-2 max-w-2xl text-gray-600">
                  Workspaces are the top-level home for your projects and members. Pick the active workspace here, then manage what belongs inside it.
                </p>
              </div>

              <div className="flex flex-col gap-3 self-start sm:flex-row sm:items-center lg:self-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/app/onboarding')}
                  className="justify-center"
                >
                  <InformationCircleIcon className="mr-2 h-5 w-5" />
                  Workspace Guide
                </Button>

                <Link to="/app/workspaces/new">
                  <Button className="flex items-center justify-center">
                    <PlusIcon className="h-5 w-5 mr-2" />
                    New Workspace
                  </Button>
                </Link>
              </div>
            </div>

            <Card className="mb-6 border-blue-100 bg-blue-50 p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-blue-900">How the hierarchy works</h2>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div className="rounded-xl bg-white/80 p-4">
                  <p className="text-sm font-medium text-blue-900">1. Workspace</p>
                  <p className="mt-1 text-sm text-blue-800">A workspace groups the people and project work for one client, department, or initiative.</p>
                </div>
                <div className="rounded-xl bg-white/80 p-4">
                  <p className="text-sm font-medium text-blue-900">2. Projects</p>
                  <p className="mt-1 text-sm text-blue-800">Projects live inside the active workspace and stay separate when you switch workspaces.</p>
                </div>
                <div className="rounded-xl bg-white/80 p-4">
                  <p className="text-sm font-medium text-blue-900">3. Members</p>
                  <p className="mt-1 text-sm text-blue-800">Members belong to a workspace and control who can view or edit the projects inside it.</p>
                </div>
              </div>
            </Card>

            <div className="space-y-4">
              {sortedWorkspaces.length === 0 ? (
                <Card className="p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UsersIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No workspaces yet</h3>
                  <p className="text-gray-600 mb-6">
                    Create your first workspace to organize projects and invite members into a shared space.
                  </p>
                  <Link to="/app/workspaces/new">
                    <Button>
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Create Workspace
                    </Button>
                  </Link>
                </Card>
              ) : (
                sortedWorkspaces.map((workspace) => {
                  const isActive = workspace.workspaceId === currentWorkspace?.workspaceId;
                  const isSwitching = switchingTo === workspace.workspaceId;
                  const isActionLoading = actionWorkspaceId === workspace.workspaceId;
                  const isBusy = isSwitching || isActionLoading;
                  const creditBalance = workspaceCredits[workspace.workspaceId];
                  const remainingCreditsSummary = creditBalance
                    ? `${creditBalance.creditsRemaining.toLocaleString()} credits remaining`
                    : isCreditsLoading
                      ? 'Loading credits...'
                      : 'Credits unavailable';

                  return (
                    <Card
                      key={workspace.workspaceId}
                      role={isActive ? undefined : 'button'}
                      tabIndex={isActive ? undefined : 0}
                      aria-label={isActive ? undefined : `Switch to ${workspace.name}`}
                      onClick={isActive ? undefined : () => handleSwitchWorkspace(workspace.workspaceId)}
                      onKeyDown={
                        isActive
                          ? undefined
                          : (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                void handleSwitchWorkspace(workspace.workspaceId);
                              }
                            }
                      }
                      className={`p-6 transition-all ${isActive ? 'ring-2 ring-blue-500' : 'cursor-pointer hover:shadow-md'} ${isBusy ? 'opacity-90' : ''}`}
                    >
                      <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
                        <div className="flex items-start flex-1">
                          <div
                            className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                              isActive ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <span className="font-bold text-lg">
                              {workspace.name?.charAt(0)?.toUpperCase() || 'W'}
                            </span>
                          </div>

                          <div className="ml-4 flex-1">
                            <div className="flex flex-wrap items-center gap-3">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {workspace.name || 'Untitled Workspace'}
                              </h3>
                              {isActive && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  <CheckIcon className="h-3 w-3 mr-1" />
                                  Active Workspace
                                </span>
                              )}
                            </div>

                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                              <span>{formatWorkspaceRole(workspace.role)}</span>
                              <span aria-hidden="true">•</span>
                              <span>{workspace.projectCount} projects</span>
                              <span aria-hidden="true">•</span>
                              <span>{workspace.memberCount} members</span>
                              <span aria-hidden="true">•</span>
                              <span className="font-mono text-xs">{workspace.workspaceId.substring(0, 8)}...</span>
                            </div>

                            <p className="mt-4 text-sm text-gray-600">
                              {workspace.description || 'Projects and members in this workspace stay grouped together when you switch contexts.'}
                            </p>

                            <p className="mt-2 text-sm text-gray-500">{remainingCreditsSummary}</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleManageMembers(workspace.workspaceId);
                            }}
                            disabled={isBusy}
                          >
                            <UsersIcon className="mr-2 h-4 w-4" />
                            {isActionLoading ? 'Opening...' : workspace.role === 'owner' ? 'Manage Members' : 'View Members'}
                          </Button>

                          {workspace.role === 'owner' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleAllocateCredits(workspace.workspaceId);
                                }}
                                disabled={isBusy}
                              >
                                <CreditCardIcon className="mr-2 h-4 w-4" />
                                {isActionLoading ? 'Opening...' : 'Allocate Credits'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleEditWorkspace(workspace.workspaceId);
                                }}
                                disabled={isBusy}
                              >
                                <PencilSquareIcon className="mr-2 h-4 w-4" />
                                Edit Workspace
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        </main>
      </div>

      {editWorkspace && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-xl font-semibold text-gray-900">Edit Workspace</h2>
            <p className="mt-2 text-sm text-gray-600">
              Update the name and description for this workspace without switching away from your current context.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="workspace-edit-name">Workspace Name</Label>
                <Input
                  id="workspace-edit-name"
                  value={editWorkspace.name}
                  onChange={(event) => setEditWorkspace((current) => current ? { ...current, name: event.target.value } : current)}
                  className="mt-1"
                  placeholder="Enter workspace name"
                />
              </div>

              <div>
                <Label htmlFor="workspace-edit-description">Description</Label>
                <textarea
                  id="workspace-edit-description"
                  rows={4}
                  value={editWorkspace.description}
                  onChange={(event) => setEditWorkspace((current) => current ? { ...current, description: event.target.value } : current)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-primary"
                  placeholder="What kind of work belongs in this workspace?"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditWorkspace(null)}
                disabled={isSavingWorkspace}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveWorkspace}
                disabled={isSavingWorkspace}
              >
                {isSavingWorkspace ? 'Saving...' : 'Save Workspace'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <CreditAllocationModal
        isOpen={allocationModal.isOpen}
        onClose={() => setAllocationModal({ isOpen: false, workspaceId: null, workspaceName: '' })}
        onAllocate={handleModalAllocate}
        workspaceName={allocationModal.workspaceName}
        maxCredits={userCredits?.creditsAvailable || 0}
        isAllocating={isAllocatingCredits}
      />
    </div>
  );
}

